import React from 'react';
import { Box, Container, Link, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { READING_WIDTH } from '../../../theme/tokens';

export type HomeSectionVariant = 'plain' | 'subtle' | 'brand';

interface HomeSectionAction {
  label: string;
  onClick: () => void;
}

interface HomeSectionProps {
  id?: string;
  /** plain: fundo branco; subtle: neutro claro; brand: tingido com a cor da entidade. */
  variant?: HomeSectionVariant;
  overline?: string;
  title?: string;
  subtitle?: string;
  /** Link "Ver todas" alinhado à direita do título. */
  action?: HomeSectionAction;
  children: React.ReactNode;
}

const backgrounds: Record<HomeSectionVariant, string> = {
  plain: 'background.default',
  subtle: 'background.subtle',
  brand: 'brand.surface',
};

/**
 * Faixa de seção da home: fundo de largura total, conteúdo no container
 * e cabeçalho padronizado (overline, título h2, subtítulo e ação).
 */
export default function HomeSection({ id, variant = 'plain', overline, title, subtitle, action, children }: HomeSectionProps) {
  const hasHeading = !!(overline || title || subtitle);
  const headingId = id && title ? `${id}-titulo` : undefined;

  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={headingId}
      sx={{
        backgroundColor: backgrounds[variant],
        py: { xs: 6, md: 9 },
      }}
    >
      <Container maxWidth="xl">
        {(hasHeading || action) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'flex-end' },
              justifyContent: 'space-between',
              gap: { xs: 1.5, sm: 3 },
              mb: { xs: 3, md: 4 },
            }}
          >
            <Box sx={{ maxWidth: READING_WIDTH }}>
              {overline && (
                <Typography variant="overline" component="p" sx={{ color: 'brand.text', mb: 0.5 }}>
                  {overline}
                </Typography>
              )}
              {title && (
                <Typography variant="h2" component="h2" id={headingId}>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>

            {action && (
              <Link
                component="button"
                onClick={action.onClick}
                underline="none"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  minHeight: 44,
                  flexShrink: 0,
                  color: 'brand.text',
                  fontSize: '1rem',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  '& .arrow-icon': { transition: 'transform 200ms' },
                  '&:hover': {
                    textDecoration: 'underline',
                    '& .arrow-icon': { transform: 'translateX(3px)' },
                  },
                }}
              >
                {action.label}
                <ArrowForwardIcon className="arrow-icon" sx={{ fontSize: '1.125rem' }} />
              </Link>
            )}
          </Box>
        )}
        {children}
      </Container>
    </Box>
  );
}
